import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from "axios";
import {
  AppDeleteModal,
  AppModal,
  AppNoInfo,
} from '@/layout/AppComponents';
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../../components/helpers/Translator';
import InecoRiskManagerRiskActionCard from './InecoRiskManagerRiskActionCard';
import InecoRiskManagerRiskInterventionCard from './InecoRiskManagerRiskInterventionCard';
import InecoRiskManagerRiskActionForm from './InecoRiskManagerRiskActionForm';
import InecoRiskManagerRiskInterventionForm from './InecoRiskManagerRiskInterventionForm';

export default function TrackingTab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const tracking = useSelector((s) => s.currentPage?.trackingTab?.[elementID]);
  const permissionLevel = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState();
  const [showDeleteActionModal, setShowDeleteActionModal] = useState();
  const [showInterventionModal, setShowInterventionModal] = useState();
  const [
    showDeleteInterventionModal,
    setShowDeleteInterventionModal,
  ] = useState();

  // > LOAD TRACKING TAB
  const loadTracking = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_inecoRiskManager_risk_tracking_tab"),
          { moduleBridgeHex, riskID: elementID }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                "trackingTab",
                {
                  [elementID]: data?.actions,
                },
                true
              )
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID]
  );

  useEffect(() => {
    if (isLoading) loadTracking();
  }, [isLoading]);
  // < LOAD TRACKING TAB

  // > DELETE ACTION
  const handleDeleteAction = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_inecoRiskManager_risk_action_delete"),
          {
            moduleBridgeHex,
            riskID: elementID,
            actionID: showDeleteActionModal,
          }
        )
        .then(() => {
          if (isMounted.current) {
            setShowActionModal(undefined);
            setShowDeleteActionModal(undefined);
            setIsLoading(true);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, elementID, showDeleteActionModal]
  );
  // < DELETE ACTION

  // > DELETE INTERVENTION
  const handleDeleteIntervention = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_inecoRiskManager_risk_intervention_delete"
          ),
          {
            moduleBridgeHex,
            riskID: elementID,
            interventionID: showDeleteInterventionModal,
          }
        )
        .then(() => {
          if (isMounted.current) {
            setShowInterventionModal(undefined);
            setShowDeleteInterventionModal(undefined);
            setIsLoading(true);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, elementID, showDeleteInterventionModal]
  );
  // < DELETE INTERVENTION

  const hasEditPermission = useCallback(() => {
    if (permissionLevel === 0) return true;
  }, [tracking, permissionLevel]);

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <div className="container-fluid">
      {hasEditPermission && (
        <div className="row mt-3">
          <div className="col-12 mb-3">
            <button
              type="button"
              className="btn btn-xs btn-success"
              onClick={() => setShowActionModal(0)}
            >
              Nueva actividad / acción
            </button>
            <AppModal
              show={
                showActionModal !== undefined &&
                showDeleteActionModal === undefined
              }
              title={
                showActionModal === 0
                  ? 'Nueva Acción'
                  : `Editar Acción con ID: ${showActionModal}`
              }
              size="xl"
              onClose={() => setShowActionModal(undefined)}
            >
              <InecoRiskManagerRiskActionForm
                riskID={elementID}
                actionID={showActionModal}
                tracking={tracking}
                showDeleteActionModal={showDeleteActionModal}
                onShowDeleteActionModal={setShowDeleteActionModal}
                onCloseModals={() => {
                  setShowActionModal(undefined);
                  setShowInterventionModal(undefined);
                  setShowDeleteActionModal(undefined);
                  setShowDeleteInterventionModal(undefined);
                }}
                onReloadTab={() => setIsLoading(true)}
              />
            </AppModal>
            <AppDeleteModal
              show={showDeleteActionModal}
              title={`Eliminar acción con ID ${showActionModal}`}
              size="lg"
              onClose={() => setShowDeleteActionModal(undefined)}
              onConfirm={handleDeleteAction}
            />
          </div>
        </div>
      )}

      {tracking?.map((a) => (
        <div className="row">
          <div className="col-lg-6 mb-3">
            <InecoRiskManagerRiskActionCard
              action={a}
              onShowActionModal={() => setShowActionModal(a?.id)}
              onShowInterventionModal={() =>
                setShowInterventionModal({ actionID: a?.id, interventionID: 0 })
              }
            />
          </div>
          <div className="col-lg-6">
            <div className="row">
              {a?.interventions?.map((i) => (
                <div className="col-12 mb-3">
                  <InecoRiskManagerRiskInterventionCard
                    intervention={i}
                    onShowInterventionModal={() =>
                      setShowInterventionModal({
                        actionID: a?.id,
                        interventionID: i?.id,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      <AppModal
        show={
          showInterventionModal !== undefined &&
          showDeleteInterventionModal === undefined
        }
        title={
          showInterventionModal?.interventionID === 0
            ? 'Nueva Actuación'
            : `Editar Actuación con ID: ${showInterventionModal?.interventionID}`
        }
        size="xl"
        onClose={() => setShowInterventionModal(undefined)}
      >
        <InecoRiskManagerRiskInterventionForm
          riskID={elementID}
          actionID={showInterventionModal?.actionID}
          interventionID={showInterventionModal?.interventionID}
          tracking={tracking}
          showDeleteActionModal={showDeleteActionModal}
          onShowDeleteInterventionModal={setShowDeleteInterventionModal}
          onCloseModals={() => {
            setShowActionModal(undefined);
            setShowInterventionModal(undefined);
            setShowDeleteActionModal(undefined);
            setShowDeleteInterventionModal(undefined);
          }}
          onReloadTab={() => setIsLoading(true)}
        />
      </AppModal>
      <AppDeleteModal
        show={showDeleteInterventionModal}
        title={`Eliminar itervención con ID ${showDeleteInterventionModal}`}
        size="lg"
        onClose={() => setShowDeleteInterventionModal(undefined)}
        onConfirm={handleDeleteIntervention}
      />
    </div>
  );
}
